import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import SiteDocumentationService from '@/services/siteDocumentation';
import type { SiteDocument, SiteDocumentationRow } from '@/types/siteDocumentation';

interface SiteDocumentationState {
  data: SiteDocumentationRow[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  fetched: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: SiteDocumentationState = {
  data: [],
  loading: false,
  error: null,
  totalCount: 0,
  fetched: false,
  status: 'idle',
};

// Async thunk for fetching site documentation
export const fetchSiteDocumentation = createAsyncThunk<
  { results: SiteDocumentationRow[]; totalCount: number },
  { userId: string; siteId: string; page?: number; pageSize?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' | null }
>(
  'siteDocumentation/fetchSiteDocumentation',
  async ({ userId, siteId, page = 1, pageSize = 100, sortBy, sortOrder }) => {
    const siteDocumentationService = new SiteDocumentationService();
    const response = await siteDocumentationService.getSiteDocumentation(page, pageSize, sortBy, sortOrder);
    console.log('Fetched site documentation:', response);
    const mappedRows = response.results.map((row: SiteDocument) => ({
      id: row.id,
      name: row.name,
      type:
      typeof row.customer_sites_report_type === 'string'
      ? row.customer_sites_report_type
      : row.customer_sites_report_type?.name || '',
      location: row.location?.name || '',
      report_date: row.report_date,
      upload_date: row.upload_date,
      file_name: row.file_name,
      modified_by: row.modified_by,
      original_data: {
        id: row.id,
        name: row.name,
        type:
        typeof row.customer_sites_report_type === 'string'
        ? row.customer_sites_report_type
        : row.customer_sites_report_type?.name || '',
        type_id: typeof row.customer_sites_report_type === 'object' ? row.customer_sites_report_type?.id || 0 : 0,
        location: row.location?.name || '',
        location_id: row.location?.id || 0,
        report_date: row.report_date,
        upload_date: row.upload_date,
        uploaded_date: row.uploaded_date,
      },
    }));
    
    return {
      results: mappedRows,
      totalCount: response.count || mappedRows.length,
    };
  }
);

const siteDocumentationSlice = createSlice({
  name: 'siteDocumentation',
  initialState,
  reducers: {
    clearSiteDocumentation: (state) => {
      state.data = [];
      state.error = null;
      state.totalCount = 0;
      state.fetched = false;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteDocumentation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fetched = false;
        state.status = 'loading';
      })
      .addCase(fetchSiteDocumentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.results;
        state.totalCount = action.payload.totalCount;
        state.error = null;
        state.fetched = true;
        state.status = 'succeeded';
      })
      .addCase(fetchSiteDocumentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load site documentation';
        state.data = [];
        state.fetched = true;
        state.status = 'failed';
      });
  },
});

export const { clearSiteDocumentation } = siteDocumentationSlice.actions;
export default siteDocumentationSlice.reducer;
